import { throwError as observableThrowError, Observable, of } from 'rxjs';
import { catchError, shareReplay, map, tap } from 'rxjs/operators';
import { Injectable, EventEmitter, Output, NgZone } from '@angular/core'
//import { Headers} from '@angular/http' //Http,
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Router } from '@angular/router'
import { UserModel, LoginResponse  } from '../insa-models/user.model';
import { SharedMethodsModule } from '../insa-custom-scripts/sharedMethods';
import { appConfig } from '../../insaweb.config';
import { JwtHelper } from './ng2_jwt';
import { RegistrationFields } from '../insa-models';

// 'login'  - normal sign-in
// 'reset'  - forgot password (not logged in)
// 'change' - change password from Settings (already logged in)
export type TwoFactorPurpose = 'login' | 'reset' | 'change';

@Injectable()
export class AuthService {

  private hostAddress = appConfig.authServiceAddress;
  private servicesURL = "/api/Account";
  private loggedIn: boolean = false;
  private loaded: boolean = false;
  private expires: number;
  roles: string;
  auth_token: string;
  sessionId: string;
  username: string = '';
  UserInfo: UserModel;
  changeSucceeded: boolean = false;
  passChangeMessage: string;
  registrationSucceeded: boolean = false
  embeddedToken: boolean = false;
  jwtHelper: JwtHelper = new JwtHelper();

  @Output() LoggedInStatusChangedEmitter = new EventEmitter();
  @Output() UserInfoEmitter = new EventEmitter();
  @Output() registrationSuccessEmitter = new EventEmitter();
  @Output() activationSuccessEmitter = new EventEmitter();



  constructor(private http: HttpClient, private _router: Router, private _zone: NgZone) {
    let tempToken: string | null = localStorage.getItem('accessToken');
    this.loggedIn = false;
    if (tempToken) {
      this.performAuthenticaton(tempToken);
      if (this.loggedIn) {
        this.getUserInfo(true, true);
      }
    }
  }



  login(username: string, password: string, jwt = '') {
  const headers = this.getAuthHeaders('application/x-www-form-urlencoded');
  const jwtquery = jwt ? '&jwt=' + encodeURIComponent(jwt) : '';

  this.http.post<LoginResponse>(
    this.hostAddress + '/Login',
    'grant_type=password&username=' + encodeURIComponent(username) +
    '&password=' + encodeURIComponent(password) +
    jwtquery,
    { headers }
  ).pipe(
    shareReplay(1),
    catchError(err => {
      const culture = localStorage.getItem('selected-locale') || (appConfig as any).defaultLocale || 'en-US';
      return this.handleError(err, appConfig.messages?.couldNotLogIn?.[culture] || 'Could not log in. Please check username and/or password!');
    })
  ).subscribe(async (res: LoginResponse) => {
    if (!res) {
      return;
    }

    this.roles = res.Roles;

    const twoFactorSetupCompleted = this.toBoolean(res.TwoFactorCompleted);
    const twoFactorRequired = this.toBoolean(res.twoFactorRequired);
    const authStage = res.auth_stage || '';

    const loginType = appConfig.TwoFactorAuthLoginType ? 'TOTP' : '';


    if (authStage === 'preauth' || twoFactorRequired) {
      // purpose defaults to 'login' - this is the normal sign-in path
      this.storePreTwoFactorState(res, 'login');

      const isPrivatePartner = this.toBoolean(res.isPrivatePartner);
      if (isPrivatePartner) {
        if (!twoFactorSetupCompleted) {
          await this._router.navigate(['/twoFactorSetup']);
        } else {
          await this._router.navigate(['/twoFactorValidate']);
        }
      }else{
        await this._router.navigate(['/twoFactorCertificate']);
      }

      return;
    }


    this.performAuthenticaton(res.access_token);
    this.getUserInfo(true, false);
  });
}

  private toBoolean(value: any): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return false;
}

/**
 * Shared routing for the password flows ('reset' / 'change') once the
 * backend has answered with a preauth response. Same rules login() uses:
 * private partner -> TOTP screen, otherwise -> certificate screen.
 * A response with canSelfServe === false (account has no 2FA enrolled) goes
 * to /twoFactorSetup, whose guard shows the blocked / "contact support"
 * screen - a password flow must never be able to enroll new 2FA.
 */
private routeToPasswordFlowVerifier(res: LoginResponse): void {
  const isPrivatePartner = this.toBoolean((res as any).isPrivatePartner);
  const twoFactorSetupCompleted = this.toBoolean((res as any).TwoFactorCompleted);
  const canSelfServe = (res as any).canSelfServe !== false;

  if (!canSelfServe || (isPrivatePartner && !twoFactorSetupCompleted)) {
    this._router.navigate(['/twoFactorSetup']);
  } else if (isPrivatePartner) {
    this._router.navigate(['/twoFactorValidate']);
  } else {
    this._router.navigate(['/twoFactorCertificate']);
  }
}

// -----------------------------------------------------------------------
// Forgot password (reuses the existing 2FA verification screens)
// -----------------------------------------------------------------------

/**
 * Kicks off the "forgot password" flow. This intentionally mirrors login()'s
 * preauth branch: the backend tells us whether the user is a private partner
 * (TOTP) or not (certificate), and we store pre-2FA state tagged with
 * purpose = 'reset' so the shared 2FA components know NOT to log the user in
 * once verification succeeds.
 *
 * SECURITY NOTE: the backend endpoint for this MUST return a uniform
 * response (status/timing/body) regardless of whether the username exists,
 * to avoid username enumeration. See discussion - this method assumes the
 * backend already does that; the frontend does not attempt to distinguish
 * "unknown user" from "known user" itself.
 */
requestPasswordReset(username: string): Observable<LoginResponse> {
  const headers = this.getAuthHeaders('application/x-www-form-urlencoded');

  // Backend: AccountController.RequestPasswordReset(string userName).
  // Body is empty - the options object must be the THIRD argument.
  const req = this.http.post<LoginResponse>(
    this.hostAddress + this.servicesURL + '/RequestPasswordReset?userName=' + encodeURIComponent(username),
    null,
    { headers }
  ).pipe(
    shareReplay(1),
    catchError(err => this.handleErrorNoRedirect(err, 'Could not initiate password reset. Please try again.'))
  );

  req.subscribe((res: LoginResponse) => {
    if (!res) {
      return;
    }

    this.storePreTwoFactorState(res, 'reset');
    this.routeToPasswordFlowVerifier(res);
  });

  return req;
}

/**
 * Issues the actual password reset once 2FA/certificate verification has
 * succeeded. Uses the short-lived reset token (NOT auth_token) so the user
 * is never granted a real session as a side effect of resetting a password.
 */
resetPassword(resetToken: string, newPassword: string): Observable<any> {
  const headers = new HttpHeaders()
    .set('Authorization', 'Bearer ' + resetToken)
    .set('Access-Control-Allow-Headers', 'Authorization')
    .set('Content-Type', 'application/x-www-form-urlencoded');

  return this.http.post(
    this.hostAddress + this.servicesURL + '/ResetPassword',
    'newPassword=' + encodeURIComponent(newPassword),
    { headers }
  ).pipe(
    shareReplay(1)
    // Deliberately no catchError/handleError here: on failure we want the
    // ResetPasswordComponent to show the error inline and stay on screen,
    // not get redirected to /login by handleError(). The component's own
    // subscribe({ error: ... }) handles it.
  );
}

// Short-lived token proving the user completed 2FA for a password-reset
// purpose. Kept separate from auth_token/accessToken at all times.
setResetToken(token: string): void {
  localStorage.setItem('reset_token', token);
}

getResetToken(): string | null {
  return localStorage.getItem('reset_token');
}

clearResetToken(): void {
  localStorage.removeItem('reset_token');
}

// -----------------------------------------------------------------------
// Change password from Settings (user is logged in; still requires the
// same 2FA verification screens before the password can be changed)
// -----------------------------------------------------------------------

/**
 * Kicks off the logged-in "change password" flow (Settings -> Special
 * Functions -> Change Password). Called with the normal session token
 * (getAuthHeaders uses auth_token), exactly like getUserInfo().
 *
 * Mirrors requestPasswordReset(): the backend answers with a purpose="change"
 * preauth ticket + isPrivatePartner, we store it as pre-2FA state tagged
 * 'change' and route to the TOTP or certificate screen. The logged-in
 * session (accessToken) is left untouched, so Cancel simply returns to the
 * app.
 *
 * When the account can't self-serve (no 2FA enrolled) nothing is stored or
 * navigated - the caller (SettingsComponent) shows res.message instead.
 *
 * No catchError/handleError here (same reasoning as resetPassword): a
 * failure must not bounce a logged-in user to /login; the caller's
 * subscribe({ error }) shows the server message inline.
 */
requestPasswordChange(): Observable<any> {
  const headers = this.getAuthHeaders('application/x-www-form-urlencoded');

  return this.http.post<any>(
    this.hostAddress + this.servicesURL + '/RequestPasswordChange',
    null,
    { headers }
  ).pipe(
    tap((res: any) => {
      if (!res || res.canSelfServe === false || !res.access_token) {
        return;
      }

      this.clearPasswordChangeToken();
      this.storePreTwoFactorState(res, 'change');
      this.routeToPasswordFlowVerifier(res);
    }),
    shareReplay(1)
  );
}

/**
 * Final step of the change flow. Uses the short-lived change token minted by
 * the 2FA/certificate step (NOT auth_token). The backend also re-checks the
 * current password and, on success, revokes all of the user's sessions.
 */
changePasswordVerified(changeToken: string, currentPassword: string, newPassword: string): Observable<any> {
  const headers = new HttpHeaders()
    .set('Authorization', 'Bearer ' + changeToken)
    .set('Access-Control-Allow-Headers', 'Authorization')
    .set('Content-Type', 'application/x-www-form-urlencoded');

  return this.http.post(
    this.hostAddress + this.servicesURL + '/ChangePasswordVerified',
    'currentPassword=' + encodeURIComponent(currentPassword) +
    '&newPassword=' + encodeURIComponent(newPassword),
    { headers }
  ).pipe(
    shareReplay(1)
    // No catchError: ChangePasswordVerifiedComponent shows errors inline.
  );
}

// Short-lived token proving the user completed 2FA for a password-change
// purpose. Kept separate from auth_token/accessToken and from reset_token.
setPasswordChangeToken(token: string): void {
  localStorage.setItem('pwchange_token', token);
}

getPasswordChangeToken(): string | null {
  return localStorage.getItem('pwchange_token');
}

clearPasswordChangeToken(): void {
  localStorage.removeItem('pwchange_token');
}

// -----------------------------------------------------------------------
// Pre-2FA state (shared by login, forgot-password and change-password flows)
// -----------------------------------------------------------------------

storePreTwoFactorState(res: LoginResponse, purpose: TwoFactorPurpose = 'login'): void {
  localStorage.setItem('pre2fa_access_token', res.access_token);
  localStorage.setItem('pre2fa_sessionid', res.SessionID);
  localStorage.setItem('pre2fa_username', res.username);
  localStorage.setItem('pre2fa_roles', res.Roles);
  localStorage.setItem('pre2fa_purpose', purpose);
}

clearPreTwoFactorState(): void {
  localStorage.removeItem('pre2fa_access_token');
  localStorage.removeItem('pre2fa_sessionid');
  localStorage.removeItem('pre2fa_username');
  localStorage.removeItem('pre2fa_roles');
  localStorage.removeItem('pre2fa_purpose');
}

hasPreTwoFactorState(): boolean {
  return !!localStorage.getItem('pre2fa_access_token');
}

getPreTwoFactorToken(): string | null {
  return localStorage.getItem('pre2fa_access_token');
}

/** Tells the shared 2FA components what success should do:
 *  'login'  - log the user in,
 *  'reset'  - hand back a reset token and go to /resetPassword,
 *  'change' - hand back a change token and go to /changePasswordVerified.
 * Defaults to 'login' if unset, so existing flows behave exactly as before. */
getTwoFactorPurpose(): TwoFactorPurpose {
  return (localStorage.getItem('pre2fa_purpose') as TwoFactorPurpose) || 'login';
}


  performAuthenticaton(token: string) {
    this.loggedIn = !this.jwtHelper.isTokenExpired(token);
    //this.loggedIn = (token !== "");
    if (this.loggedIn) {
      var decoded = this.jwtHelper.decodeToken(token);
      this.auth_token = token;
      this.sessionId = decoded.SessionID;
      this.username = decoded.userID || '';
      this.embeddedToken = (decoded.embeddedToken ? true : false);
      this.expires = (this.jwtHelper.getTokenExpirationDate(token).valueOf() - new Date().valueOf()) / 1000;
      setTimeout(this.logout.bind(this), this.expires * 1000);
      localStorage.setItem('accessToken', this.auth_token);
      localStorage.setItem('sessionid', this.sessionId);
    } else {
      this.logout();
    }
  }

  // This function is used to allow login from within WPF embedded browser.
  // Function should be called only within 1 specific route only WPF uses
  // That route is navigate by WPF after the embedded browser is initialized
  // We set a non-expiring token in this case :)
  embeddedLogin(access_token) {
    console.log('Embedded Login initialized')
    console.log('Logging in user...');
    // token valid, login user
    this.performAuthenticaton(access_token);
    this.sessionId = 'INSA_EMBEDDED';
    this.expires = 60 * 60 * 24 * 30; // expire in 30 days, this should be enough
    this.roles = undefined; // GetUserInfo will fill it in!
    localStorage.setItem('sessionid', this.sessionId);
    this.getUserInfo(true, true);
    return true;
  }

  changePassword(username, password, newPassword) {
    let headers = this.getAuthHeaders('application/x-www-form-urlencoded');

    this.http.post(this.hostAddress + this.servicesURL + '/ChangePassword?userName=' + encodeURIComponent(username) + '&password=' + encodeURIComponent(password) + '&newPassword=' + encodeURIComponent(newPassword),
      { headers }).pipe(shareReplay(1),
        catchError(err => this.handleError(err, "Could not change password. Please check old username and/or password!")),
        map((res) => {
          //  this.changeSucceeded = res.ChangeSucceeded
          if (this.changeSucceeded) {
            this.loggedIn = false;
            //  this.passChangeMessage = res.Message;
            this.emitLogedInStatusChange();
          } else {
            console.log("Error occured!")
          }
        })).subscribe();
  }

  register(fields: RegistrationFields) {
    setTimeout(() => {
      this.registrationSuccessEmitter.emit(true)
    }, 2000)
  }

  verifyActivationCode(activationCode: string) {
    setTimeout(() => {
      this.activationSuccessEmitter.emit(true)
    }, 2000)
  }

  getUserInfo(doEmitLoginChange = false, preventRedirect = false) {
    let headers = this.getAuthHeaders('application/json');
    let timeStamp = +new Date();

    let req = this.http.get(
      this.hostAddress + this.servicesURL + '/UserInfo' + '?tsp=' + timeStamp,
      { headers }).pipe(shareReplay(1),
        catchError(err => this.handleError(err, "Could not get user info ")))
    req.subscribe((data: UserModel) => {
      this.UserInfo = data;
      if (this.roles === undefined) {
        // TODO: sort the roles or something?
        this.roles = this.UserInfo.Roles.find(() => true);
      }
      this.UserInfoEmitter.emit(this.UserInfo);
      // emit login change for embedded login scenario
      if (doEmitLoginChange) {
        this.loggedIn = true;
        this.emitLogedInStatusChange(preventRedirect, this.embeddedToken);
      }
    });
    return req;
  }

  logout() {
    this.auth_token = "";
    this.sessionId = "";
    this.username = "";
    this.loggedIn = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('sessionid');
    // A pending password change belongs to the session that started it.
    localStorage.removeItem('pwchange_token');
    this._zone.run(() => this.emitLogedInStatusChange());
  }

  isLoggedIn() {
    return this.loggedIn;
  }

  public emitLogedInStatusChange(preventRedirect = false, embeddedMode = false) {
    this.LoggedInStatusChangedEmitter.emit({ value: this.loggedIn, preventRedirect: preventRedirect, embeddedMode: embeddedMode });
  }

  getAuthHeaders(contentType: string): HttpHeaders {
    let headers = new HttpHeaders()
      .set('Authorization', 'Bearer ' + this.auth_token)
      .set('Access-Control-Allow-Headers', 'Authorization')
      .set('Content-Type', contentType);
    //.set('Accept', contentType)

    /*headers.append('Authorization', 'Bearer ' + this.auth_token);
    headers.append('Access-Control-Allow-Headers', 'Authorization');
    headers.append('Content-Type', contentType + 'charset=utf-8');
    //headers.append('Accept', contentType);*/
    return headers;
  }

  public handleError(error: any, message: string) {
    let errMsg = (error.message) ? error.message :
      error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    console.error(errMsg);
    kendo.ui.progress($(".main-loading"), false);
    SharedMethodsModule.addNotification("#notification", message, false);
    this._router.navigate(['/login']);
    return observableThrowError(errMsg);

  }

  /**
   * Same as handleError, but does NOT redirect to /login. Used by flows
   * (like forgot-password initiation) where a failure should keep the user
   * on the current screen rather than bouncing them away.
   */
  public handleErrorNoRedirect(error: any, message: string) {
    let errMsg = (error.message) ? error.message :
      error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    console.error(errMsg);
    kendo.ui.progress($(".main-loading"), false);
    return observableThrowError(errMsg);
  }

  public loadDefaultGridLayout(gridName: string, metadataName) {
    const key = metadataName ? `${gridName}/${metadataName}/defaultLayout` : `${gridName}/defaultLayout`
    const defaultLayout = JSON.parse(localStorage.getItem(key))
    return of(defaultLayout || { name: 'default' });
  }

  public saveDefaultGridLayout(gridName: string, metadataName: string, gridOptions: kendo.ui.GridOptions) {
    const key = metadataName ? `${gridName}/${metadataName}/defaultLayout` : `${gridName}/defaultLayout`
    const options = { name: 'default', options: gridOptions };
    localStorage.setItem(key, JSON.stringify(options))
  }

}



// WEBPACK FOOTER //
// ./app/insa-services/auth.service.ts